import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { PageLoader, EmptyState } from "@/components/Loading";
import { categoryLabel } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { GraduationCap, Plus, Trash2, CalendarDays, Clock, Building2, User, Users, ClipboardCheck, Check } from "lucide-react";
import { toast } from "sonner";

export default function Lectures() {
  const { user } = useAuth();
  const [lectures, setLectures] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [units, setUnits] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ program_id: "", title: "", lecturer_id: "", date: "", start_time: "", end_time: "", unit_id: "", gender: "both", hours: 1, reminders: [1440, 60, 15] });
  const [attendOpen, setAttendOpen] = useState(false);
  const [attendLec, setAttendLec] = useState(null);
  const [students, setStudents] = useState([]);
  const [presentIds, setPresentIds] = useState([]);

  const load = () => api.get("/lectures").then((r) => setLectures(r.data)).catch(() => setLectures([]));
  useEffect(() => {
    load();
    api.get("/programs").then((r) => setPrograms(r.data)).catch(() => {});
    api.get("/units").then((r) => setUnits(r.data)).catch(() => {});
    if (user.role === "admin") api.get("/teachers").then((r) => setTeachers(r.data)).catch(() => {});
  }, []);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    try {
      await api.post("/lectures", { ...form, hours: Number(form.hours), lecturer_id: form.lecturer_id || null, unit_id: form.unit_id || null });
      toast.success("تمت إضافة المحاضرة"); setOpen(false);
      setForm({ program_id: "", title: "", lecturer_id: "", date: "", start_time: "", end_time: "", unit_id: "", gender: "both", hours: 1, reminders: [1440, 60, 15] });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => { if (!window.confirm("حذف المحاضرة؟")) return; try { await api.delete(`/lectures/${id}`); toast.success("تم الحذف"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  const openAttend = async (l) => {
    setAttendLec(l); setAttendOpen(true); setStudents([]); setPresentIds([]);
    try {
      const [en, rec] = await Promise.all([
        api.get(`/enrollments?program_id=${l.program_id}`),
        api.get(`/attendance/records?lecture_id=${l.id}`),
      ]);
      setStudents(en.data.filter((e) => e.status === "approved"));
      setPresentIds(rec.data.map((r) => r.student_id));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const markPresent = async (studentId) => {
    try {
      await api.post("/attendance/manual", { lecture_id: attendLec.id, student_id: studentId });
      setPresentIds((p) => [...p, studentId]); toast.success("تم تسجيل الحضور"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (lectures === null) return <PageLoader />;
  const canManage = user.role === "admin" || user.role === "supervisor" || user.role === "teacher";

  return (
    <>
      <PageHeader title="المحاضرات" subtitle="جدولة وإدارة محاضرات البرامج"
        action={canManage && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="add-lecture-btn" className="rounded-xl bg-navy hover:bg-navy-light"><Plus className="me-2 h-4 w-4" /> إضافة محاضرة</Button></DialogTrigger>
            <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto">
              <DialogHeader><DialogTitle className="font-heading text-navy">إضافة محاضرة</DialogTitle></DialogHeader>
              <div className="grid gap-4 py-2 sm:grid-cols-2">
                <div className="sm:col-span-2"><Label>البرنامج</Label>
                  <Select value={form.program_id} onValueChange={set("program_id")}><SelectTrigger data-testid="lec-program" className="mt-1.5 rounded-lg"><SelectValue placeholder="اختر البرنامج" /></SelectTrigger>
                    <SelectContent>{programs.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2"><Label>عنوان المحاضرة</Label><Input data-testid="lec-title" value={form.title} onChange={(e) => set("title")(e.target.value)} className="mt-1.5 rounded-lg" /></div>
                {user.role === "admin" && (
                  <div className="sm:col-span-2"><Label>المحاضر</Label>
                    <Select value={form.lecturer_id} onValueChange={set("lecturer_id")}><SelectTrigger className="mt-1.5 rounded-lg"><SelectValue placeholder="اختر المحاضر" /></SelectTrigger>
                      <SelectContent>{teachers.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                <div><Label>التاريخ</Label><Input data-testid="lec-date" type="date" value={form.date} onChange={(e) => set("date")(e.target.value)} className="mt-1.5 rounded-lg" /></div>
                <div><Label>عدد الساعات</Label><Input type="number" step="0.5" value={form.hours} onChange={(e) => set("hours")(e.target.value)} className="mt-1.5 rounded-lg" /></div>
                <div><Label>وقت البداية</Label><Input type="time" value={form.start_time} onChange={(e) => set("start_time")(e.target.value)} className="mt-1.5 rounded-lg" /></div>
                <div><Label>وقت النهاية</Label><Input type="time" value={form.end_time} onChange={(e) => set("end_time")(e.target.value)} className="mt-1.5 rounded-lg" /></div>
                <div><Label>الوحدة السكنية</Label>
                  <Select value={form.unit_id} onValueChange={set("unit_id")}><SelectTrigger data-testid="lec-unit" className="mt-1.5 rounded-lg"><SelectValue placeholder="اختر" /></SelectTrigger>
                    <SelectContent>{units.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>الفئة</Label>
                  <Select value={form.gender} onValueChange={set("gender")}><SelectTrigger className="mt-1.5 rounded-lg"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="male">ذكور</SelectItem><SelectItem value="female">إناث</SelectItem><SelectItem value="both">ذكور وإناث</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2"><Label>تذكيرات المحاضرة</Label>
                  <div className="mt-1.5 flex flex-wrap gap-2">
                    {[{ v: 1440, l: "قبل يوم" }, { v: 60, l: "قبل ساعة" }, { v: 15, l: "قبل 15 دقيقة" }].map((o) => {
                      const on = form.reminders.includes(o.v);
                      return (
                        <button type="button" key={o.v} data-testid={`reminder-${o.v}`}
                          onClick={() => setForm((f) => ({ ...f, reminders: on ? f.reminders.filter((x) => x !== o.v) : [...f.reminders, o.v] }))}
                          className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${on ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
                          {o.l}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
              <DialogFooter><Button data-testid="lec-save" onClick={submit} disabled={!form.program_id || !form.title || !form.date} className="rounded-xl bg-navy hover:bg-navy-light">حفظ</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      />
      {lectures.length === 0 ? <EmptyState icon={GraduationCap} title="لا توجد محاضرات" desc="أضف محاضرة لبدء جدولة البرنامج." /> : (
        <div className="stagger grid gap-4">
          {lectures.map((l) => (
            <div key={l.id} data-testid={`lecture-${l.id}`} className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-slate-100 bg-white p-5 soft-shadow card-hover">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">{l.program_name}</span>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">{categoryLabel[l.gender]}</span>
                </div>
                <h3 className="mt-2 font-heading text-lg font-bold text-navy">{l.title}</h3>
                <div className="mt-2 flex flex-wrap items-center gap-4 text-xs text-slate-400">
                  <span className="flex items-center gap-1"><User className="h-3.5 w-3.5" /> {l.lecturer_name || "—"}</span>
                  <span className="flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" /> {l.date}</span>
                  <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {l.start_time} - {l.end_time} ({l.hours}س)</span>
                  <span className="flex items-center gap-1"><Building2 className="h-3.5 w-3.5" /> {l.unit_name || "—"}</span>
                  <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {l.attendance_count} حاضر</span>
                </div>
              </div>
              {canManage && (
                <div className="flex items-center gap-2">
                  <Button onClick={() => openAttend(l)} data-testid={`attend-lecture-${l.id}`} size="sm" variant="outline" className="rounded-lg border-emerald-200 text-emerald-600 hover:bg-emerald-50"><ClipboardCheck className="me-1 h-4 w-4" /> الحضور</Button>
                  <Button onClick={() => remove(l.id)} data-testid={`del-lecture-${l.id}`} size="sm" variant="ghost" className="text-red-400 hover:bg-red-50 hover:text-red-500 h-8 w-8 p-0"><Trash2 className="h-4 w-4" /></Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={attendOpen} onOpenChange={setAttendOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-navy">تسجيل الحضور اليدوي — {attendLec?.title}</DialogTitle></DialogHeader>
          <div className="space-y-2 py-2">
            {students.length === 0 ? <p className="py-6 text-center text-sm text-slate-400">لا يوجد طلاب معتمدون في هذا البرنامج</p> : students.map((s) => {
              const present = presentIds.includes(s.student_id);
              return (
                <div key={s.id} data-testid={`attend-student-${s.student_id}`} className="flex items-center justify-between rounded-xl border border-slate-100 px-3 py-2">
                  <span className="text-sm font-semibold text-navy">{s.student_name}</span>
                  {present ? (
                    <span className="flex items-center gap-1 rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700"><Check className="h-3.5 w-3.5" /> حاضر</span>
                  ) : (
                    <Button size="sm" data-testid={`mark-present-${s.student_id}`} onClick={() => markPresent(s.student_id)} className="rounded-lg bg-greenx hover:bg-greenx-light">تسجيل حضور</Button>
                  )}
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
